import type { FC, SVGProps } from 'react';

import clsx from 'clsx';

import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreSubscriptionLevel,
} from '../types/SubscriptionLevel';
import { FanIcon } from './icons/FanIcon';
import { FollowerIcon } from './icons/FollowerIcon';
import { SubscriberIcon } from './icons/SubscriberIcon';
import { VipIcon } from './icons/VipIcon';

type Level = PopStoreSubscriptionLevel | PopStoreExtendedSubscriptionLevel;

interface Props {
  level: Level;
  /** Hide the label text and render the icon alone. */
  iconOnly?: boolean;
  className?: string;
}

interface TierStyle {
  Icon: FC<SVGProps<SVGSVGElement>>;
  label: string;
  /**
   * Tailwind gradient + text-color classes for the pill. Optional:
   * FOLLOWER has an icon but NO pill (RN's badge image is "unavailable
   * for now"), so it renders icon-only and never as a gradient pill.
   */
  pill?: string;
}

// Every tier — FOLLOWER included — is a table entry, mirroring RN's
// `kSubscriberBadgeImageMap` (one entry per level, `[source, Icon, …]`).
// FOLLOWER simply omits `pill` (RN's "source unavailable"), which the single
// render path below reads as "icon-only, no gradient".
const kTiers: Record<Level, TierStyle | undefined> = {
  [PopStoreExtendedSubscriptionLevel.FOLLOWER]: {
    Icon: FollowerIcon,
    label: 'Follower',
  },
  [PopStoreSubscriptionLevel.SUBSCRIBER]: {
    Icon: SubscriberIcon,
    label: 'Subscriber',
    pill: 'theme-keep from-subscription-badge-subscriber-base via-subscription-badge-subscriber-light to-subscription-badge-subscriber-dark text-white',
  },
  [PopStoreSubscriptionLevel.FAN]: {
    Icon: FanIcon,
    label: 'Fan',
    pill: 'theme-keep from-subscription-badge-fan-base via-subscription-badge-fan-light to-subscription-badge-fan-dark text-white',
  },
  [PopStoreSubscriptionLevel.VIP]: {
    Icon: VipIcon,
    label: 'VIP',
    pill: 'from-subscription-badge-vip-base via-subscription-badge-vip-light to-subscription-badge-vip-dark theme-keep text-black',
  },
};

/**
 * Visual badge for a creator-subscription tier (Follower / Subscriber /
 * Fan / VIP). Renders an icon + label inside a horizontal-gradient pill;
 * colors come from `subscription-badge-*` Tailwind tokens (ported from
 * the PopshopRN SVG gradient definitions).
 *
 * Single-table render keyed by the tier's `pill`, NOT by tier name:
 * - A tier WITH a `pill` (Subscriber / Fan / VIP) → the gradient pill,
 *   with the label hidden in `iconOnly` mode (unchanged behavior).
 * - A tier with NO `pill` (FOLLOWER) → the bare icon in `iconOnly` mode
 *   (RN gives FOLLOWER an icon but no pill), else `null`.
 */
export function SubscriptionLevelBadge({ level, iconOnly, className }: Props) {
  const tier = kTiers[level];
  if (!tier) return null;

  const { Icon, label, pill } = tier;

  // No pill design for this tier (FOLLOWER): show the bare icon in icon-only
  // mode, otherwise nothing (there's no pill to render the label inside).
  if (!pill) {
    return iconOnly ? (
      <Icon className={clsx('h-3 w-3 shrink-0', className)} />
    ) : null;
  }

  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center gap-1 rounded-md bg-gradient-to-r px-1.5 py-0.5 text-[11px] font-medium leading-none',
        pill,
        className,
      )}
    >
      <Icon className="h-3 w-3" />
      {iconOnly ? null : <span>{label}</span>}
    </span>
  );
}
